import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";
import { getRequest, postRequest, putRequest, deleteRequest } from "Includes/api/apicall";
import { GET_URL, POST_URL, PUT_URL, DEL_URL } from "Includes/urls";
import { AddCircleOutlineOutlined } from "@mui/icons-material";

function ParamsList(props) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "" });
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getRequest(GET_URL.client_track_params.api, {}, props);
      if (res && res.data) {
        setRows(Array.isArray(res.data) ? res.data : (res.data.data || []));
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleOpen = (item = null) => {
    if (item) {
      setEditId(item.id);
      setFormData({ name: item.name || "" });
    } else {
      setEditId(null);
      setFormData({ name: "" });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      Swal.fire("Error", "Module name is required", "error");
      return;
    }
    const payload = {
      name: formData.name,
    };

    setLoading(true);
    try {
      if (editId) {
        await putRequest(`${PUT_URL.client_track_params.api}${editId}/`, payload, props);
        Swal.fire("Success", "Module updated successfully", "success");
      } else {
        await postRequest(POST_URL.client_track_params.api, payload, props);
        Swal.fire("Success", "Module created successfully", "success");
      }
      handleClose();
      fetchData();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "Something went wrong", "error");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await deleteRequest(`${DEL_URL.client_track_params.api}${id}/`, {}, props);
          Swal.fire('Deleted!', 'Module has been deleted.', 'success');
          fetchData();
        } catch (e) {
          console.error(e);
          Swal.fire("Error", "Failed to delete module", "error");
        }
        setLoading(false);
      }
    });
  };

  const columns = [
    { field: "name", headerName: "Module Name", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <Button size="small" onClick={() => handleOpen(params.row)}>Edit</Button>
          <Button size="small" color="error" onClick={() => handleDelete(params.row.id)}>Delete</Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="bold">Client Tracking - Modules</Typography>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineOutlined />}
          onClick={() => handleOpen()}
        >
          Add Module
        </Button>
      </Box>
      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Module" : "Create Module"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Module Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ParamsList;
